import { create } from "zustand";
import type {
  SiteDocument,
  SiteNode,
  Page,
  Theme,
  SiteMeta,
  ComponentType,
} from "@zirro/schema";
import { createEmptyDocument, createPage } from "@zirro/schema";
import {
  updateNodePropsInDocument,
  addNodeToDocument,
  insertSectionTreeToDocument,
  removeNodeFromDocument,
  duplicateNodeInDocument,
  moveNodeInDocument,
  reorderNodeInDocument,
  cloneNodeKeepingRoles,
  findNodeById,
  migrateLegacyOverrides,
} from "./tree-utils";
import { clampZoom, effectiveZoom, stepZoom, type Zoom } from "./zoom";
import { imageFileProblem, readAsDataUrl, replaceImageProps } from "./image-frame";

export type Viewport = "desktop" | "tablet" | "mobile";
export type SaveStatus = "saved" | "saving" | "unsaved" | "error";
export type LeftTab = "pages" | "add" | "layers";
export type InspectorTab = "content" | "style" | "theme";
export type EditorMode = "edit" | "preview";

/** An alternative design for a page, offered by the host app. */
export interface PageLayoutOption {
  id: string;
  /** Slug of the page this layout is for, e.g. "/" or "/shop". */
  pageSlug: string;
  name: string;
  description?: string;
  nodes: SiteNode[];
}

export function saveDraftToStorage(siteId: string, doc: SiteDocument): void {
  try {
    if (!siteId || !doc) return;
    localStorage.setItem(`zirro_draft_${siteId}`, JSON.stringify(doc));
  } catch (err) {
    console.warn("Failed to write draft to localStorage:", err);
  }
}

export function loadDraftFromStorage(siteId: string): SiteDocument | null {
  try {
    if (!siteId) return null;
    const raw = localStorage.getItem(`zirro_draft_${siteId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.pages) && parsed.pages.length > 0) {
      return parsed as SiteDocument;
    }
  } catch (err) {
    console.warn("Failed to read draft from localStorage:", err);
  }
  return null;
}

const VIEWPORTS: readonly Viewport[] = ["desktop", "tablet", "mobile"];

/** Remembers the editor viewport per site, so a refresh reopens the same view. */
export function saveViewportToStorage(siteId: string, viewport: Viewport): void {
  try {
    localStorage.setItem(`zirro_viewport_${siteId}`, viewport);
  } catch (err) {
    console.warn("Failed to write viewport to localStorage:", err);
  }
}

export function loadViewportFromStorage(siteId: string): Viewport {
  try {
    const stored = localStorage.getItem(`zirro_viewport_${siteId}`);
    if (VIEWPORTS.includes(stored as Viewport)) return stored as Viewport;
  } catch (err) {
    console.warn("Failed to read viewport from localStorage:", err);
  }
  return "desktop";
}

export interface EditorState {
  // Site ID tracking for persistence
  siteId: string | null;

  // Document & Mode state
  document: SiteDocument;
  activePageId: string;
  selectedNodeId: string | null;
  hoveredNodeId: string | null;
  inlineEditingNodeId: string | null;
  mode: EditorMode;
  viewport: Viewport;
  /** Canvas zoom; "fit" follows the canvas width (see fitZoom). */
  zoom: Zoom;
  /** The scale that fits the page in the canvas, measured by the canvas. */
  fitZoom: number;
  leftTab: LeftTab;
  inspectorTab: InspectorTab;

  // Sidebar adjustable dimensions & collapse states
  leftSidebarWidth: number;
  rightSidebarWidth: number;
  leftSidebarCollapsed: boolean;
  rightSidebarCollapsed: boolean;

  // Drag state
  draggedType: ComponentType | null;
  draggedNodeId: string | null;

  /** Alternative layouts the host app offers for pages (by page slug). */
  pageLayouts: PageLayoutOption[];

  // History stacks
  history: SiteDocument[];
  future: SiteDocument[];

  // Save status
  saveStatus: SaveStatus;
  /** Why the last save failed, in words the user can act on; null otherwise. */
  saveError: string | null;
  /** Bumped by retrySave so autosave tries again without a new edit. */
  saveAttempt: number;
  isDirty: boolean;
  lastSavedAt: Date | null;

  // Media Modal state
  isMediaModalOpen: boolean;
  onSelectMediaCallback: ((url: string) => void) | null;
  /** Stores an image file and returns its URL; without one, files become data URLs. */
  imageUploader: ((file: File) => Promise<string>) | null;

  // Actions
  setSiteId: (siteId: string | null) => void;
  setDocument: (document?: SiteDocument, siteId?: string) => void;
  setActivePage: (pageId: string) => void;
  selectNode: (nodeId: string | null) => void;
  setHoveredNodeId: (nodeId: string | null) => void;
  setInlineEditingNodeId: (nodeId: string | null) => void;
  setMode: (mode: EditorMode) => void;
  setViewport: (viewport: Viewport) => void;
  setZoom: (zoom: Zoom) => void;
  setFitZoom: (fitZoom: number) => void;
  zoomBy: (direction: 1 | -1) => void;
  setLeftTab: (tab: LeftTab) => void;
  setInspectorTab: (tab: InspectorTab) => void;
  setLeftSidebarWidth: (width: number) => void;
  setRightSidebarWidth: (width: number) => void;
  toggleLeftSidebar: () => void;
  toggleRightSidebar: () => void;
  setDraggedType: (type: ComponentType | null) => void;
  setDraggedNodeId: (nodeId: string | null) => void;

  // Document Mutators (pushed to history stack)
  updateNodeProps: (
    nodeId: string,
    props: Record<string, unknown>,
    breakpoint?: Viewport,
  ) => void;
  addNode: (
    parentContainerId: string | null,
    type: ComponentType,
    index?: number,
  ) => void;
  insertSectionTemplate: (sectionTree: SiteNode, index?: number) => void;
  removeNode: (nodeId: string) => void;
  duplicateNode: (nodeId: string) => void;
  moveNode: (nodeId: string, direction: "up" | "down") => void;
  reorderNode: (
    sourceId: string,
    targetId: string,
    position: "before" | "after" | "inside",
  ) => void;

  // Page Management Mutators
  addPage: (title: string, slug?: string) => void;
  updatePage: (pageId: string, patch: Partial<Page>) => void;
  /** Swaps a page's content for a layout's nodes (undoable). */
  replacePageNodes: (pageId: string, nodes: SiteNode[]) => void;
  setPageLayouts: (layouts: PageLayoutOption[]) => void;
  deletePage: (pageId: string) => void;
  reorderPages: (pageIds: string[]) => void;

  // Theme & Meta Mutators
  updateTheme: (themePatch: Partial<Theme>) => void;
  updateSiteMeta: (metaPatch: Partial<SiteMeta>) => void;

  // History Controls
  undo: () => void;
  redo: () => void;

  // Save State Controls
  /** Sets the save status; `error` is the message shown for status "error". */
  setSaveStatus: (status: SaveStatus, error?: string | null) => void;
  markSaved: () => void;
  retrySave: () => void;

  // Media Modal Controls
  openMediaModal: (onSelect: (url: string) => void) => void;
  closeMediaModal: () => void;
  setImageUploader: (uploader: ((file: File) => Promise<string>) | null) => void;
  /** Checks and stores an image file, returning the URL to use. Throws a user-facing Error. */
  uploadImage: (file: File) => Promise<string>;
  /**
   * Puts a new photo into a node: an Image keeps its frame (see
   * replaceImageProps), a Section gets a new background, a Header a new logo.
   * `renderedRatio` is the old photo's on-page width/height.
   */
  replaceImage: (nodeId: string, src: string, renderedRatio?: number | null) => void;
}

const MAX_HISTORY = 50;

/**
 * Rapid edits to the same props of the same node (typing, dragging a slider
 * or a focal point) share one undo step. `base` is the history entry the
 * first edit pushed; once anything else changes history, edits stop merging.
 */
const MERGE_EDITS_MS = 800;
let lastPropEdit: { key: string; at: number; base: SiteDocument } | null = null;

export const useEditorStore = create<EditorState>((set, get) => {
  const persistDraft = (doc: SiteDocument) => {
    const sId = get().siteId;
    if (sId) {
      saveDraftToStorage(sId, doc);
    }
  };

  return {
    siteId: null,
    document: createEmptyDocument("My Website"),
    activePageId: "page-home",
    selectedNodeId: null,
    hoveredNodeId: null,
    inlineEditingNodeId: null,
    mode: "edit",
    viewport: "desktop",
    zoom: "fit",
    fitZoom: 1,
    leftTab: "pages",
    inspectorTab: "content",

    leftSidebarWidth: 320,
    rightSidebarWidth: 320,
    leftSidebarCollapsed: false,
    rightSidebarCollapsed: false,

    draggedType: null,
    draggedNodeId: null,

    pageLayouts: [],
    history: [],
    future: [],

    saveStatus: "saved",
    saveError: null,
    saveAttempt: 0,
    isDirty: false,
    lastSavedAt: null,

    isMediaModalOpen: false,
    onSelectMediaCallback: null,
    imageUploader: null,

    setSiteId: (siteId) => {
      const currentSiteId = get().siteId;
      if (currentSiteId === siteId) return;
      set({ siteId, viewport: siteId ? loadViewportFromStorage(siteId) : "desktop" });
      if (siteId) {
        const localDoc = loadDraftFromStorage(siteId);
        if (localDoc) {
          get().setDocument(localDoc, siteId);
        }
      }
    },

    setDocument: (document, siteId) => {
      const targetSiteId = siteId ?? get().siteId;
      let doc = document;
      if (typeof doc === "string") {
        try {
          doc = JSON.parse(doc);
        } catch {
          doc = undefined;
        }
      }

      // Fallback hierarchy: passed document -> local storage draft -> existing store document (if valid) -> empty default
      const safeDoc = migrateLegacyOverrides(
        doc && Array.isArray((doc as SiteDocument).pages) && (doc as SiteDocument).pages.length > 0
          ? (doc as SiteDocument)
          : (targetSiteId ? loadDraftFromStorage(targetSiteId) : null) ??
            (get().document && get().document.pages.length > 0
              ? get().document
              : createEmptyDocument("My Website")),
      );

      const activePage =
        safeDoc.pages.find((p) => p.slug === "/") ?? safeDoc.pages[0];

      if (targetSiteId) {
        saveDraftToStorage(targetSiteId, safeDoc);
      }

      set({
        siteId: targetSiteId,
        document: safeDoc,
        activePageId: activePage?.id ?? "page-home",
        selectedNodeId: null,
        hoveredNodeId: null,
        inlineEditingNodeId: null,
        history: [],
        future: [],
        saveStatus: "saved",
        saveError: null,
        isDirty: false,
      });
    },

    setActivePage: (pageId) => {
      set({ activePageId: pageId, selectedNodeId: null, inlineEditingNodeId: null });
    },

    selectNode: (nodeId) => {
      set({
        selectedNodeId: nodeId,
        inspectorTab: nodeId ? "content" : "theme",
      });
    },

    setHoveredNodeId: (hoveredNodeId) => set({ hoveredNodeId }),
    setInlineEditingNodeId: (inlineEditingNodeId) => set({ inlineEditingNodeId }),
    setMode: (mode) => set({ mode, selectedNodeId: mode === "preview" ? null : get().selectedNodeId, inlineEditingNodeId: null }),
    setViewport: (viewport) => {
      const sId = get().siteId;
      if (sId) saveViewportToStorage(sId, viewport);
      set({ viewport });
    },
    setZoom: (zoom) => set({ zoom: zoom === "fit" ? zoom : clampZoom(zoom) }),
    setFitZoom: (fitZoom) => {
      if (get().fitZoom !== fitZoom) set({ fitZoom });
    },
    zoomBy: (direction) => {
      const { zoom, fitZoom } = get();
      set({ zoom: stepZoom(effectiveZoom(zoom, fitZoom), direction) });
    },
    setLeftTab: (leftTab) => set({ leftTab, leftSidebarCollapsed: false }),
    setInspectorTab: (inspectorTab) => set({ inspectorTab, rightSidebarCollapsed: false }),
    setLeftSidebarWidth: (width) => set({ leftSidebarWidth: Math.max(240, Math.min(600, width)) }),
    setRightSidebarWidth: (width) => set({ rightSidebarWidth: Math.max(260, Math.min(600, width)) }),
    toggleLeftSidebar: () => set((s) => ({ leftSidebarCollapsed: !s.leftSidebarCollapsed })),
    toggleRightSidebar: () => set((s) => ({ rightSidebarCollapsed: !s.rightSidebarCollapsed })),
    setDraggedType: (draggedType) => set({ draggedType }),
    setDraggedNodeId: (draggedNodeId) => set({ draggedNodeId }),

    updateNodeProps: (nodeId, props, breakpoint = "desktop") => {
      const { document, history } = get();
      const newDoc = updateNodePropsInDocument(
        document,
        nodeId,
        (prevProps, prevResponsive) => {
          if (breakpoint === "desktop") {
            return { props: { ...prevProps, ...props }, responsive: prevResponsive };
          }
          const responsive = { ...(prevResponsive ?? {}) };
          responsive[breakpoint] = {
            ...(responsive[breakpoint] ?? {}),
            ...props,
          };
          return { props: prevProps, responsive };
        },
      );

      persistDraft(newDoc);
      const key = `${nodeId}|${breakpoint}|${Object.keys(props).sort().join(",")}`;
      const now = Date.now();
      const merge =
        lastPropEdit !== null &&
        lastPropEdit.key === key &&
        now - lastPropEdit.at < MERGE_EDITS_MS &&
        history[history.length - 1] === lastPropEdit.base;
      if (merge) {
        lastPropEdit!.at = now;
        set({ document: newDoc, future: [], isDirty: true, saveStatus: "unsaved" });
        return;
      }
      lastPropEdit = { key, at: now, base: document };
      set({
        document: newDoc,
        history: [...history.slice(-MAX_HISTORY), document],
        future: [],
        isDirty: true,
        saveStatus: "unsaved",
      });
    },

    addNode: (parentContainerId, type, index) => {
      const { document, activePageId, history } = get();
      const { document: newDoc, newNode } = addNodeToDocument(
        document,
        parentContainerId,
        activePageId,
        type,
        index,
      );

      persistDraft(newDoc);
      set({
        document: newDoc,
        selectedNodeId: newNode.id,
        inspectorTab: "content",
        history: [...history.slice(-MAX_HISTORY), document],
        future: [],
        isDirty: true,
        saveStatus: "unsaved",
      });
    },

    insertSectionTemplate: (sectionTree, index) => {
      const { document, activePageId, history } = get();
      const { document: newDoc, newSection } = insertSectionTreeToDocument(
        document,
        activePageId,
        sectionTree,
        index,
      );

      persistDraft(newDoc);
      set({
        document: newDoc,
        selectedNodeId: newSection.id,
        inspectorTab: "content",
        history: [...history.slice(-MAX_HISTORY), document],
        future: [],
        isDirty: true,
        saveStatus: "unsaved",
      });
    },

    removeNode: (nodeId) => {
      const { document, selectedNodeId, history } = get();
      const newDoc = removeNodeFromDocument(document, nodeId);
      persistDraft(newDoc);
      set({
        document: newDoc,
        selectedNodeId: selectedNodeId === nodeId ? null : selectedNodeId,
        history: [...history.slice(-MAX_HISTORY), document],
        future: [],
        isDirty: true,
        saveStatus: "unsaved",
      });
    },

    duplicateNode: (nodeId) => {
      const { document, history } = get();
      const { document: newDoc, newNode } = duplicateNodeInDocument(document, nodeId);
      if (!newNode) return;
      persistDraft(newDoc);
      set({
        document: newDoc,
        selectedNodeId: newNode.id,
        history: [...history.slice(-MAX_HISTORY), document],
        future: [],
        isDirty: true,
        saveStatus: "unsaved",
      });
    },

    moveNode: (nodeId, direction) => {
      const { document, history } = get();
      const newDoc = moveNodeInDocument(document, nodeId, direction);
      persistDraft(newDoc);
      set({
        document: newDoc,
        history: [...history.slice(-MAX_HISTORY), document],
        future: [],
        isDirty: true,
        saveStatus: "unsaved",
      });
    },

    reorderNode: (sourceId, targetId, position) => {
      const { document, history } = get();
      const newDoc = reorderNodeInDocument(document, sourceId, targetId, position);
      persistDraft(newDoc);
      set({
        document: newDoc,
        history: [...history.slice(-MAX_HISTORY), document],
        future: [],
        isDirty: true,
        saveStatus: "unsaved",
      });
    },

    addPage: (title, slug) => {
      const { document, history } = get();
      if (document.pages.length >= 20) return; // PRD §38 limit
      const newPage = createPage(title, slug);
      const newPages = [...document.pages, newPage];
      const newDoc = { ...document, pages: newPages };

      persistDraft(newDoc);
      set({
        document: newDoc,
        activePageId: newPage.id,
        selectedNodeId: null,
        history: [...history.slice(-MAX_HISTORY), document],
        future: [],
        isDirty: true,
        saveStatus: "unsaved",
      });
    },

    updatePage: (pageId, patch) => {
      const { document, history } = get();
      const newPages = document.pages.map((p) => {
        if (p.id !== pageId) return p;
        // Prevent changing Home page slug from "/"
        if (p.slug === "/" && patch.slug && patch.slug !== "/") {
          delete patch.slug;
        }
        return { ...p, ...patch };
      });
      const newDoc = { ...document, pages: newPages };

      persistDraft(newDoc);
      set({
        document: newDoc,
        history: [...history.slice(-MAX_HISTORY), document],
        future: [],
        isDirty: true,
        saveStatus: "unsaved",
      });
    },

    replacePageNodes: (pageId, nodes) => {
      const { document, history } = get();
      const fresh = nodes.map((node) => cloneNodeKeepingRoles(node));
      const newDoc = {
        ...document,
        pages: document.pages.map((p) => (p.id === pageId ? { ...p, nodes: fresh } : p)),
      };
      persistDraft(newDoc);
      set({
        document: newDoc,
        history: [...history.slice(-MAX_HISTORY), document],
        future: [],
        selectedNodeId: null,
        inlineEditingNodeId: null,
        isDirty: true,
        saveStatus: "unsaved",
      });
    },

    setPageLayouts: (pageLayouts) => set({ pageLayouts }),

    deletePage: (pageId) => {
      const { document, activePageId, history } = get();
      const targetPage = document.pages.find((p) => p.id === pageId);
      if (!targetPage || targetPage.slug === "/") return; // Home page cannot be deleted (PRD §36)
      if (document.pages.length <= 1) return;

      const newPages = document.pages.filter((p) => p.id !== pageId);
      const homePage = newPages.find((p) => p.slug === "/") ?? newPages[0];
      const newDoc = { ...document, pages: newPages };

      persistDraft(newDoc);
      set({
        document: newDoc,
        activePageId: activePageId === pageId ? homePage.id : activePageId,
        selectedNodeId: null,
        history: [...history.slice(-MAX_HISTORY), document],
        future: [],
        isDirty: true,
        saveStatus: "unsaved",
      });
    },

    reorderPages: (pageIds) => {
      const { document, history } = get();
      const pageMap = new Map(document.pages.map((p) => [p.id, p]));
      const newPages: Page[] = [];

      for (const id of pageIds) {
        const p = pageMap.get(id);
        if (p) newPages.push(p);
      }
      // Append any missing pages
      for (const p of document.pages) {
        if (!newPages.includes(p)) newPages.push(p);
      }

      const newDoc = { ...document, pages: newPages };

      persistDraft(newDoc);
      set({
        document: newDoc,
        history: [...history.slice(-MAX_HISTORY), document],
        future: [],
        isDirty: true,
        saveStatus: "unsaved",
      });
    },

    updateTheme: (themePatch) => {
      const { document, history } = get();
      const newTheme = {
        ...document.theme,
        ...themePatch,
        colors: { ...(document.theme.colors ?? {}), ...(themePatch.colors ?? {}) },
        typography: {
          ...(document.theme.typography ?? {}),
          ...(themePatch.typography ?? {}),
        },
      };
      const newDoc = { ...document, theme: newTheme };

      persistDraft(newDoc);
      set({
        document: newDoc,
        history: [...history.slice(-MAX_HISTORY), document],
        future: [],
        isDirty: true,
        saveStatus: "unsaved",
      });
    },

    updateSiteMeta: (metaPatch) => {
      const { document, history } = get();
      const newMeta = { ...(document.meta ?? { title: "" }), ...metaPatch };
      const newDoc = { ...document, meta: newMeta };

      persistDraft(newDoc);
      set({
        document: newDoc,
        history: [...history.slice(-MAX_HISTORY), document],
        future: [],
        isDirty: true,
        saveStatus: "unsaved",
      });
    },

    undo: () => {
      const { history, document, future } = get();
      if (history.length === 0) return;
      const previous = history[history.length - 1];
      const newHistory = history.slice(0, history.length - 1);

      persistDraft(previous);
      set({
        document: previous,
        history: newHistory,
        future: [document, ...future],
        isDirty: true,
        saveStatus: "unsaved",
      });
    },

    redo: () => {
      const { future, document, history } = get();
      if (future.length === 0) return;
      const next = future[0];
      const newFuture = future.slice(1);

      persistDraft(next);
      set({
        document: next,
        history: [...history, document],
        future: newFuture,
        isDirty: true,
        saveStatus: "unsaved",
      });
    },

    setSaveStatus: (saveStatus, error = null) =>
      set({ saveStatus, saveError: saveStatus === "error" ? error : null }),

    markSaved: () =>
      set({
        saveStatus: "saved",
        saveError: null,
        isDirty: false,
        lastSavedAt: new Date(),
      }),

    retrySave: () =>
      set((s) => ({ saveStatus: "saving", saveError: null, saveAttempt: s.saveAttempt + 1 })),

    openMediaModal: (onSelect) =>
      set({
        isMediaModalOpen: true,
        onSelectMediaCallback: onSelect,
      }),

    closeMediaModal: () =>
      set({
        isMediaModalOpen: false,
        onSelectMediaCallback: null,
      }),

    setImageUploader: (imageUploader) => set({ imageUploader }),

    uploadImage: async (file) => {
      const problem = imageFileProblem(file);
      if (problem) throw new Error(problem);
      const uploader = get().imageUploader;
      return uploader ? uploader(file) : readAsDataUrl(file);
    },

    replaceImage: (nodeId, src, renderedRatio = null) => {
      const found = findNodeById(get().document, nodeId);
      if (!found) return;
      const { node } = found;
      if (node.type === "Image") {
        get().updateNodeProps(nodeId, replaceImageProps(node.props ?? {}, src, renderedRatio));
      } else if (node.type === "Section") {
        get().updateNodeProps(nodeId, { backgroundImage: src });
      } else if (node.type === "Header") {
        get().updateNodeProps(nodeId, { logoSrc: src });
      }
    },
  };
});
